import React, { useState } from 'react';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { LoginButton } from '@repo/components/buttons';
import TextInput from '../TextInput';
import styles from './EmailConfirmation.module.scss';
import { completeEmailAuthWorkflow } from '@repo/utils/helpers/jwt';
import { lastValueFrom } from 'rxjs';
import { ArrowRightIcon, LoaderIcon } from '@repo/icons';
import { AccountNotFoundError, EmailInUseError, GenericError } from './Errors';
import { WordSeparator } from '../AuthOptions';

export default function EmailConfirmation({
  setLoginEmailSent,
  loginEmailRecipient,
  userHasExistingAccount,
  accountNotFound,
  setAccountNotFound,
  emailAssociatedWithExistingAccount,
  setEmailAssociatedWithExistingAccount,
}: {
  setLoginEmailSent: React.Dispatch<React.SetStateAction<boolean>>;
  loginEmailRecipient: string;
  userHasExistingAccount: boolean;
  accountNotFound: boolean;
  setAccountNotFound: React.Dispatch<React.SetStateAction<boolean>>;
  emailAssociatedWithExistingAccount: boolean;
  setEmailAssociatedWithExistingAccount: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [loginCode, setLoginCode] = useState('');
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const inOrUp = userHasExistingAccount ? 'in' : 'up';

  const login = ({ isSignUp }: { isSignUp: boolean }) => {
    setSaving(true);
    setError(undefined);
    lastValueFrom(completeEmailAuthWorkflow(loginCode, loginEmailRecipient, isSignUp))
      .then(() => {
        window.location.href = '/';
      })
      .catch((e) => {
        setSaving(false);
        setError(e.status);
        if (e.status === 404) {
          if (!isSignUp) {
            setAccountNotFound(true);
          } else {
            setEmailAssociatedWithExistingAccount(true);
          }
        }
        console.error(e);
      });
  };

  const loginWithCode = () => {
    login({ isSignUp: !userHasExistingAccount });
  };

  // const fallbackUINewAccountLogin = () => {
  //   login({ isSignUp: true });
  // };

  const returnToSignIn = () => {
    setLoginEmailSent(false);
    setAccountNotFound(false);
    setEmailAssociatedWithExistingAccount(false);
  };

  let errorUI = <GenericError />;

  if (accountNotFound) {
    errorUI = <AccountNotFoundError />;
  }

  if (emailAssociatedWithExistingAccount) {
    errorUI = <EmailInUseError email={loginEmailRecipient} />;
  }

  return (
    <div className={styles.emailConfirmation}>
      <StateBasedFadeTransition
        state={String(!!error)}
        elementByStateMap={{
          false: (
            <>
              <p>
                We sent an email to <b>{loginEmailRecipient}</b>. It has a link that will sign you{' '}
                {inOrUp}.
              </p>
              <WordSeparator />
              <p className={styles.enterCode}>Enter verification code instead</p>
              <TextInput
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value.trim())}
                placeholder="Enter code"
                invalidValueMsg="Please enter a valid code"
                validateValue={(value) => value.length == 8}
                onEnter={loginWithCode}
              />
              <LoginButton
                className={styles.codeLoginBtn}
                onClick={loginWithCode}
                disabled={saving || loginCode.length != 8}
              >
                {!saving ? (
                  `Sign ${inOrUp} with code`
                ) : (
                  <>
                    Signing {inOrUp} <LoaderIcon customColor="#fff" />
                  </>
                )}
              </LoginButton>
            </>
          ),
          true: (
            <>
              <div className={styles.errorContent}>{errorUI}</div>
              <LoginButton className={styles.backToSignInBtn} onClick={returnToSignIn}>
                <ArrowRightIcon className={styles.arrow} /> Back to Sign {inOrUp}
              </LoginButton>
            </>
          ),
        }}
      />
    </div>
  );
}
